import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { RedirectRule, Condition } from '@/types/redirectRule';
import { Box, CircularProgress, Typography } from '@mui/material';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';
import { useAuthStore } from '@/stores/authStore';
import dripService from '@/services/dripService';
import { PLUGIN_SESSION_KEY } from '@/features/genesisEvent';

/**
 * Evaluate a condition against a parameter value
 */
const evaluateCondition = (paramValue: string | null, condition: Condition): boolean => {
  const { operator, value } = condition;

  // Handle exists/!exists operators first
  if (operator === 'exists') {
    return paramValue !== null && paramValue !== '';
  }
  if (operator === '!exists') {
    return paramValue === null || paramValue === '';
  }

  // If param doesn't exist, only != can match
  if (paramValue === null) {
    return operator === '!=';
  }

  // Try numeric comparison if both values are numbers
  const paramNum = parseFloat(paramValue);
  const conditionNum = parseFloat(value);
  const isNumericComparison = !isNaN(paramNum) && !isNaN(conditionNum);

  switch (operator) {
    case '=':
      return paramValue === value;
    case '!=':
      return paramValue !== value;
    case '>':
      return isNumericComparison ? paramNum > conditionNum : paramValue > value;
    case '>=':
      return isNumericComparison ? paramNum >= conditionNum : paramValue >= value;
    case '<':
      return isNumericComparison ? paramNum < conditionNum : paramValue < value;
    case '<=':
      return isNumericComparison ? paramNum <= conditionNum : paramValue <= value;
    default:
      return false;
  }
};

const PluginRedirect = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'redirecting' | 'error'>('loading');
  const { userData, loading: isAuthLoading } = useAuthStore();

  useEffect(() => {
    // Wait for auth to finish loading before proceeding
    if (isAuthLoading) {
      return;
    }

    let errorRedirectTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const performRedirect = async () => {
      try {
        // Fetch active redirect rules sorted by priority
        const rulesRef = collection(db, 'plugin_redirects');
        const rulesQuery = query(rulesRef, where('active', '==', true), orderBy('priority', 'asc'));
        const snapshot = await getDocs(rulesQuery);

        const rules: RedirectRule[] = [];
        snapshot.forEach((doc) => {
          rules.push({ id: doc.id, ...doc.data() } as RedirectRule);
        });

        // Convert search params to object for matching
        const params: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          params[key] = value;
        });

        // Send plugin context to Drip (fire and forget - don't block redirect)
        if (userData?.email) {
          dripService.updatePluginContext(userData.email, {
            videosTotal: params.videosTotal,
            onboardingStatus: params.onboardingStatus,
            micPermission: params.micPermission,
            cameraPermission: params.cameraPermission,
            isPinned: params.isPinned,
            source: params.source,
          });
        }

        // Find first matching rule (all conditions must evaluate to true)
        const matchingRule = rules.find((rule) =>
          Object.entries(rule.conditions).every(([key, condition]) =>
            evaluateCondition(params[key] ?? null, condition),
          ),
        );

        setStatus('redirecting');

        // Mark that user came from the plugin (for Genesis Event celebration)
        sessionStorage.setItem(PLUGIN_SESSION_KEY, 'true');

        if (matchingRule) {
          // Store flag to prevent trial redirect from intercepting
          sessionStorage.setItem('pluginRedirectDestination', matchingRule.destination);

          // Redirect to the matched destination
          navigate(matchingRule.destination, { replace: true });
        } else {
          // No match found - redirect to home
          navigate('/home', { replace: true });
        }
      } catch (error) {
        if (isFirebaseOfflineError(error)) {
          errorReporter.expected('Failed to fetch plugin redirect rules (client offline)', error, {
            feature: 'firebase',
            operation: 'fetchRedirectRules',
          });
        } else {
          errorReporter.critical('Failed to fetch plugin redirect rules', error, {
            feature: 'firebase',
            operation: 'fetchRedirectRules',
          });
        }
        setStatus('error');
        // On error, redirect to home after a brief delay
        errorRedirectTimeoutId = setTimeout(() => {
          navigate('/home', { replace: true });
        }, 2000);
      }
    };

    performRedirect();

    return () => {
      if (errorRedirectTimeoutId) clearTimeout(errorRedirectTimeoutId);
    };
  }, [navigate, searchParams, isAuthLoading, userData?.email]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        backgroundColor: '#f5f5f5',
      }}
    >
      <CircularProgress size={40} />
      <Typography variant="body1" color="text.secondary">
        {status === 'loading' && 'Loading...'}
        {status === 'redirecting' && 'Redirecting...'}
        {status === 'error' && 'Something went wrong. Redirecting to home...'}
      </Typography>
    </Box>
  );
};

export default PluginRedirect;
